import { Box, Button, makeStyles, TextField, Typography } from '@material-ui/core'
import { DatePicker } from '@mui/x-date-pickers'
import axios from 'axios'
import moment from 'moment'
import MUIDataTable from 'mui-datatables'
import React, { useEffect, useState } from 'react'
import ReservationDetailsModal from './ReservationDetailsModal'
import ReservationDecisionModal from './ReservationDecisionModal'
import formatVehicleLabel from './formatVehicleLabel'
import ReservationEditModal from './ReservationEditModal'
import ReservationTableLabels from './ReservationTableLabels'
import useReservationClock, { getReservationStatus } from './reservationLifecycle'

const useStyles = makeStyles((theme) => ({
  section: { marginTop: theme.spacing(3) },
  filters: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    margin: theme.spacing(1, 0),
    '& .MuiTextField-root': { width: 200 },
    '& .MuiOutlinedInput-notchedOutline': { borderBottom: '1px solid rgba(0, 0, 0, 0.23)' }
  },
  actions: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) },
  reject: { color: theme.palette.error.main, borderColor: theme.palette.error.main }
}))

const statusLabels = {
  pending: 'Në pritje të miratimit',
  approved: 'Miratuar',
  rejected: 'Refuzuar',
  cancelled: 'Anuluar',
  completed: 'Përfunduar'
}
const dateColumn = (name, label) => ({
  name,
  label,
  options: { sort: false, customBodyRender: (value) => (value ? moment(value).format('DD/MM/YYYY') : '') }
})

const ReservationManagementGrid = ({ reloadKey, onChanged }) => {
  const classes = useStyles()
  const [reservations, setReservations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reviewError, setReviewError] = useState('')
  const [reload, setReload] = useState(0)
  const [departureDate, setDepartureDate] = useState(null)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [detailsId, setDetailsId] = useState(null)
  const [review, setReview] = useState(null)
  const [editId, setEditId] = useState(null)
  const now = useReservationClock(reservations, () => setReload((previous) => previous + 1))

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    axios
      .get('vehicle-reservations/manage')
      .then(({ data }) => {
        if (!data.success || !Array.isArray(data.data)) throw new Error('Reservations unavailable')
        if (active) setReservations(data.data)
      })
      .catch((requestError) => {
        if (active) {
          setReservations([])
          setError(requestError.response?.data?.message || 'Rezervimet nuk u ngarkuan. Provoni përsëri.')
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [reloadKey, reload])

  const startReview = (reservation, decision) => {
    setReviewError('')
    setReview({ reservation, decision })
  }
  const reviewed = (reservation) => {
    setReservations((previous) => previous.map((item) => (item._id === reservation._id ? { ...item, ...reservation } : item)))
    setReview(null)
    setPage(0)
    onChanged()
  }
  const filtered = reservations
    .map((reservation) => ({ ...reservation, status: getReservationStatus(reservation, now) }))
    .filter(
      (reservation) =>
        (!status || reservation.status === status) &&
        (!departureDate || !moment(departureDate).isValid() || moment(reservation.departureDate).isSame(departureDate, 'day'))
    )
  const columns = [
    { name: 'ownerName', label: 'Rezervuesi', options: { sort: false, customBodyRender: (value) => value || '—' } },
    dateColumn('departureDate', 'Data e nisjes'),
    { name: 'departureTime', label: 'Ora e nisjes', options: { sort: false } },
    dateColumn('returnDate', 'Data e kthimit'),
    { name: 'returnTime', label: 'Ora e kthimit', options: { sort: false } },
    {
      name: 'vehicles',
      label: 'Automjetet',
      options: { sort: false, customBodyRender: (value) => value.map(formatVehicleLabel).join(', ') }
    },
    { name: 'destination', label: 'Destinacioni', options: { sort: false } },
    { name: 'participants', label: 'Pasagjerë', options: { sort: false, customBodyRender: (value) => value.length + 1 } },
    {
      name: 'exceptionalCase',
      label: 'Rast i jashtëzakonshëm',
      options: { sort: false, customBodyRender: (value) => (value ? 'Po' : 'Jo') }
    },
    { name: 'status', label: 'Statusi', options: { sort: false, customBodyRender: (value) => statusLabels[value] || value } },
    {
      name: '_id',
      label: 'Veprimet',
      options: {
        sort: false,
        customBodyRender: (id) => {
          const reservation = reservations.find((item) => item._id === id)
          return (
            <Box className={classes.actions}>
              <Button variant='outlined' color='primary' size='small' onClick={() => setDetailsId(id)}>
                Shiko detajet
              </Button>
              {reservation?.status === 'pending' && !loading && !error && (
                <>
                  <Button variant='outlined' color='primary' size='small' onClick={() => startReview(reservation, 'approved')}>
                    Mirato
                  </Button>
                  <Button variant='outlined' size='small' className={classes.reject} onClick={() => startReview(reservation, 'rejected')}>
                    Refuzo
                  </Button>
                </>
              )}
            </Box>
          )
        }
      }
    }
  ]
  const options = {
    download: false,
    print: false,
    filter: false,
    search: false,
    viewColumns: false,
    selectableRows: 'none',
    responsive: 'scroll',
    elevation: 4,
    customToolbar: () => (
      <Box className={classes.filters}>
        <DatePicker
          label='Data e nisjes'
          inputFormat='dd/MM/yyyy'
          value={departureDate}
          onChange={(value) => {
            setDepartureDate(value)
            setPage(0)
          }}
          componentsProps={{ actionBar: { actions: ['clear'] } }}
          renderInput={(params) => <TextField {...params} id='management-departure-date' variant='outlined' size='small' />}
        />
        <TextField
          id='management-reservation-status'
          label='Statusi'
          variant='outlined'
          size='small'
          select
          SelectProps={{ native: true }}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value)
            setPage(0)
          }}
          InputLabelProps={{ shrink: true }}
        >
          <option value=''>Të gjitha</option>
          {Object.entries(statusLabels).map(([value, label]) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </TextField>
      </Box>
    ),
    page,
    rowsPerPage,
    rowsPerPageOptions: [10, 50, 100, 200],
    onChangePage: setPage,
    onChangeRowsPerPage: (value) => {
      setRowsPerPage(value)
      setPage(0)
    },
    textLabels: new ReservationTableLabels(
      loading ? 'Duke ngarkuar rezervimet…' : error ? 'Rezervimet nuk janë ngarkuar.' : 'Nuk ka rezervime'
    )
  }

  return (
    <Box className={classes.section} data-testid='reservation-management'>
      {reviewError && (
        <Typography role='alert' color='error'>
          {reviewError}
        </Typography>
      )}
      {error && (
        <Box>
          <Typography role='alert' color='error'>
            {error}
          </Typography>
          <Button onClick={() => setReload((previous) => previous + 1)}>Provo përsëri</Button>
        </Box>
      )}
      <MUIDataTable title='Menaxho rezervimet e automjeteve' data={filtered} columns={columns} options={options} />
      {detailsId && (
        <ReservationDetailsModal
          reservationId={detailsId}
          management
          onClose={() => setDetailsId(null)}
          onChanged={onChanged}
          onEdit={(id) => {
            setReviewError('')
            setDetailsId(null)
            setEditId(id)
          }}
        />
      )}
      {editId && (
        <ReservationEditModal
          reservationId={editId}
          onClose={() => setEditId(null)}
          onSaved={(reservation) => {
            setReservations((previous) => previous.map((item) => (item._id === reservation._id ? { ...item, ...reservation } : item)))
            setEditId(null)
            setPage(0)
            onChanged()
          }}
        />
      )}
      {review && (
        <ReservationDecisionModal
          {...review}
          onClose={() => setReview(null)}
          onReviewed={reviewed}
          onStale={(message) => {
            setReview(null)
            setReviewError(message)
            onChanged()
          }}
        />
      )}
    </Box>
  )
}

export default ReservationManagementGrid
